import { AlertCircle, Clock, Sparkles, X } from 'lucide-react'
import { useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { useSubscriptionInfo } from '@/lib/db/rxdb/hooks/entities/subscriptions'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import useLoginStore from '@/store/LoginStore'

interface TrialBannerProps {
  className?: string
  dismissible?: boolean
}

/**
 * TrialBanner component that shows trial status and days remaining
 * Displays prominently when user is in trial period
 *
 * @example
 * ```tsx
 * <TrialBanner dismissible />
 * ```
 */
export function TrialBanner({
  className,
  dismissible = false,
}: TrialBannerProps) {
  // Hide trial banner if subscriptions are disabled
  if (!FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS) {
    return null
  }

  const { isTrialing, trialDaysLeft, plan } = useSubscriptionInfo()
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // On the subscription page itself the plan grid is right below, so the
  // "Upgrade Now" CTA just links to the current page — hide it there.
  const onSubscriptionPage = pathname.endsWith('/settings/subscription')
  const [isDismissed, setIsDismissed] = useState(false)

  // Don't show if not trialing or dismissed
  if (!isTrialing || isDismissed) {
    return null
  }

  const handleUpgrade = () => {
    // The subscription page is workspace-scoped — a bare /settings/subscription
    // doesn't resolve. Fall back to the slugless path only if we somehow lack a
    // slug (better than a dead link).
    window.location.href = workspaceSlug
      ? `/${workspaceSlug}/settings/subscription`
      : '/settings/subscription'
  }

  const handleDismiss = () => {
    setIsDismissed(true)
  }

  // Determine variant based on days left
  const isUrgent = trialDaysLeft <= 3
  const isWarning = trialDaysLeft <= 7 && trialDaysLeft > 3

  const getBannerStyles = () => {
    // Light /10 tint backgrounds need the *saturated* color as the text (not
    // the *-foreground white, which is for solid backgrounds and is invisible
    // on a light tint).
    if (isUrgent) {
      return 'bg-destructive/10 border-destructive/30 text-destructive'
    }
    if (isWarning) {
      return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-900 dark:text-yellow-100'
    }
    return 'bg-primary/10 border-primary/30 text-primary'
  }

  const getIcon = () => {
    if (isUrgent) return <AlertCircle className="size-4" />
    if (isWarning) return <Clock className="size-4" />
    return <Sparkles className="size-4" />
  }

  const getMessage = () => {
    if (trialDaysLeft === 0) {
      return 'Your trial ends today'
    }
    if (trialDaysLeft === 1) {
      return 'Your trial ends tomorrow'
    }
    return `${trialDaysLeft} days left in your trial`
  }

  const getDescription = () => {
    if (isUrgent) {
      return 'Upgrade now to keep access to all premium features'
    }
    return `You're currently on the ${plan?.name || 'Team'} trial. Upgrade anytime to continue after your trial ends.`
  }

  return (
    <div
      className={`relative flex items-center justify-between gap-4 rounded-lg border p-4 ${getBannerStyles()} ${className}`}
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{getMessage()}</p>
          <p className="text-xs opacity-90 mt-0.5">{getDescription()}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!onSubscriptionPage && (
          <Button
            onClick={handleUpgrade}
            size="sm"
            variant={isUrgent ? 'destructive' : 'default'}
          >
            Upgrade Now
          </Button>
        )}

        {dismissible && (
          <Button
            onClick={handleDismiss}
            size="sm"
            variant="ghost"
            className="size-8 p-0"
          >
            <X className="size-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        )}
      </div>
    </div>
  )
}
